import MOBILE, { BREAKPOINT_PX } from "./mobileConfig";
import { isIOSWebKit } from "./useIsMobile";

/**
 * On an iPhone, a field taking focus does not zoom the page (MOBILE.iosNoInputZoom).
 *
 * WebKit on iOS zooms in on any input, textarea, select or contenteditable
 * whose text is under 16px the moment it takes focus, and does not zoom back
 * out when it loses it. This layer's fields are 13–14px, to the board, and
 * login's number field is `clamp(14px, 1.1vw, 17px)` — 14px on a phone. The
 * zoom also changes visualViewport's scale, which useVisualViewport's sheet
 * sizing assumes is 1.
 *
 * `maximum-scale=1` on the viewport tag is what stops it. iOS reads it as "no
 * zoom on focus", and since iOS 10 Safari IGNORES it for pinch, so a user can
 * still pinch to zoom. That keeps every font size as designed and covers every
 * kind of field at once, with no CSS.
 *
 * Why here and not in public/index.html:
 * - iOS ONLY. Android Chrome honours `maximum-scale=1` as "no pinch-zoom", so
 *   the static tag would take zoom away from every Android user.
 * - MOBILE WIDTH ONLY, like the rest of the layer. Above the breakpoint the
 *   tag goes back to exactly what it was, so an iPad — or a large iPhone in
 *   landscape — keeps today's behaviour.
 * - Width, not `zy-mobile`: the login page never stamps that class, and its
 *   number field is the first one a phone user taps.
 *
 * Started once at boot (src/index.js). Off iOS, or with the flag off, it
 * returns before touching anything.
 */

const NO_ZOOM = "maximum-scale=1";

let started = false;

export const startIosInputZoomGuard = () => {
  if (started) return;
  if (!MOBILE.ENABLED || !MOBILE.iosNoInputZoom) return;
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
  if (!isIOSWebKit()) return;
  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta) return;
  started = true;

  const original = meta.getAttribute("content") || "";
  const mql = window.matchMedia(`(max-width: ${BREAKPOINT_PX}px)`);
  const apply = () => {
    meta.setAttribute("content", mql.matches ? `${original}, ${NO_ZOOM}` : original);
  };
  apply();

  /* Same fallback as useMobileQuery: addListener is what Safari < 14 has. */
  if (typeof mql.addEventListener === "function") {
    mql.addEventListener("change", apply);
  } else {
    mql.addListener(apply);
  }
};
